import type { Fall } from "./faelle";
import { tagAmHof } from "./verteilen";
import { mitLaufname } from "./meldungstext";
import { wegHier } from "./blattweg";
import { tagKurz as tagKurzImBetrieb } from "./betriebstag";
import { leersatzFuer, type Ladestand } from "./ladestand";

/* ══════════════════════════════════════════════════════════════════
   Ein Fall in Worten — Gerät, was, wann, bei wem und warum
   (25.09.2026, Band „Was hier hakt", 51-meldungen.md).

   Luca las auf der Startseite an V-2026-0015 „Übergeben · überfällig" und
   bezog das auf die Übergabe am 29.09. — überfällig war aber die vom
   24.09., an einem anderen Gerät. Seitdem nennt jeder Punkt Gerät und Tag,
   und am Auftrag steht je Fall ein Satz, was fehlt.

   Die Sätze stehen in de.json unter `fall.warum.<art>`; wo es auf Stufe
   oder Besitz ankommt, als Unterbaum — gesucht wird vom Genauesten zum
   Allgemeinsten:

     fall.warum.<art>.<stufe>_<besitz>
     fall.warum.<art>.<stufe>
     fall.warum.<art>.<besitz>
     fall.warum.<art>.alle
     fall.warum.<art>

   Fehlt alles, steht das Wort der Art (`fall.art.<art>`) — lieber ein
   dünner Satz als keiner.
   ══════════════════════════════════════════════════════════════════ */

type T = ((pfad: string, werte?: Record<string, string | number>) => string);

const KALENDERTAG = /^\d{4}-\d{2}-\d{2}$/;

/** Der Tag am Hof: Ein Kalendertag bleibt, ein Zeitpunkt wird umgerechnet. */
export function hoftag(wert: string | null | undefined): string | null {
  if (!wert) return null;
  if (KALENDERTAG.test(wert)) return wert;
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? null : tagAmHof(d);
}

/** `24.09.` aus `2026-09-24`. */
export function tagKurz(tag: string | null | undefined): string {
  return tagKurzImBetrieb(tag);
}

/** Aufgaben, die im Brett verteilt werden. */
export const VERTEILBAR = new Set(["vorbereitung", "uebergabe", "ruecknahme", "verteilen"]);

/**
 * Der Tag, an dem der Fall dran ist oder war. Bei der Vorbereitung ist es
 * ihr eigener Tag, nicht der der Übergabe — dieselbe Regel wie im Brett.
 */
export function fallTag(f: Fall): string | null {
  if (f.art === "vorbereitung" && f.daten["vorbereiten_ab"]) return hoftag(f.daten["vorbereiten_ab"]);
  if (f.art === "verteilen" && f.daten["tag"]) return f.daten["tag"];
  return hoftag(f.faellig);
}

/** Das Wort für das, was zu tun ist — beim Verteilen die Aufgabe selbst („Übergeben"). */
export function fallWort(f: Fall, t: T): string {
  if (f.art === "verteilen" && f.daten["aufgabe"]) return t(`verteilen.art.${f.daten["aufgabe"]}`);
  return t(`fall.art.${f.art}`, mitLaufname(f.art, f.daten, t));
}

/** `24.09.` — oder `seit 24.09.`, wenn der Fall keinen Termin hat, sondern besteht. */
export function fallDatum(f: Fall, t: T): string {
  const tag = fallTag(f);
  if (tag) return tagKurz(tag);
  const seit = hoftag(f.seit);
  return seit ? t("fall.seitTag", { tag: tagKurz(seit) }) : "";
}

/** Wer den Fall hat — oder bei wem er liegt, solange ihn niemand hat. */
export function fallWer(f: Fall, t: T): string {
  if (f.besitzer) return f.besitzer.name;
  return VERTEILBAR.has(f.art) ? t("fall.nochNiemand") : t("fall.liegtBei", { gruppe: t(`meldungen.gruppe.${f.gruppe}`) });
}

/** Die Werte für den Satz — die Daten des Falls und die Tage in kurzer Form. */
function werteVon(f: Fall, t: T): Record<string, string> {
  return {
    ...f.daten,
    kennung: f.kennung ?? "",
    vorgang: f.vorgang ?? "",
    termin: tagKurz(hoftag(f.faellig)),
    tag: tagKurz(fallTag(f)),
    seit: tagKurz(hoftag(f.seit)),
    ab: tagKurz(hoftag(f.daten["vorbereiten_ab"])),
    genommen_am: tagKurz(hoftag(f.daten["genommen_am"])),
    plan_bis: tagKurz(hoftag(f.daten["plan_bis"])),
    besitzer: f.besitzer?.name ?? "",
    aufgabe: f.daten["aufgabe"] ? t(`verteilen.art.${f.daten["aufgabe"]}`) : "",
  };
}

/** Der eine Satz: was an diesem Fall fehlt. */
export function warum(f: Fall, t: T, gibt: (pfad: string) => boolean): string {
  const besitz = f.daten["besitz"] ?? (f.besitzer ? "genommen" : "frei");
  const basis = `fall.warum.${f.art}`;
  const kandidaten = [
    `${basis}.${f.stufe}_${besitz}`, `${basis}.${f.stufe}`, `${basis}.${besitz}`, `${basis}.alle`, basis,
  ];
  const werte = mitLaufname(f.art, werteVon(f, t), t);
  const pfad = kandidaten.find(gibt);
  return pfad ? t(pfad, werte) : t(`fall.art.${f.art}`, werte);
}

export type Fallweg = { wort: string; zu: string; vorgang?: string } | { wort: string; anker: string };

/** Fälle, die am Auftrag selbst weiter unten stehen. Seit Phase 4
 *  (05.10.2026) keine mehr: Die AB-Fälle zu Lexware Office sind entfallen. */
const AM_AUFTRAG_UNTEN = new Set<string>();

/**
 * **Wohin der Knopf am Auftrag führt** — dorthin, wo es erledigt wird.
 *
 * - Was noch niemand hat, wird eingeteilt, an seinem Tag
 *   (`/einteilen?tag=…`, seit dem 08.10.2026, docs/65).
 * - Vorbereiten, Übergeben, Zurücknehmen mit Besitzer: in den Kalender, auf
 *   den Auftrag gefiltert — dort wird umgeplant.
 * - Was nach Lexware Office gehört, steht unten am Auftrag.
 * - Führt der Weg des Falls auf genau diesen Auftrag, gibt es keinen Knopf:
 *   Man ist schon da.
 */
export function fallWeg(f: Fall, hier: string, anker = true): Fallweg | null {
  const nr = f.vorgang ?? "";
  if (VERTEILBAR.has(f.art)) {
    const tag = fallTag(f);
    if (!f.besitzer && tag) return { wort: "fall.weg.verteilen", zu: `/einteilen?tag=${tag}` };
    if (nr) return { wort: "fall.weg.brett", zu: "/kalender", vorgang: nr };
  }
  if (anker && AM_AUFTRAG_UNTEN.has(f.art)) return { wort: "fall.weg.unten", anker: "lex-hinweise" };
  if (!f.weg) return null;
  /* Derselbe Auftrag, aber mit dem Blatt dazu (`?protokoll=`, `?fotos=`,
     D-22): Dann heißt der Knopf „Aufschlagen", nicht „Zum Auftrag". */
  const lage = wegHier(f.weg, hier);
  if (lage === "keiner") return null;
  if (lage === "blatt") return { wort: "fall.weg.blatt", zu: f.weg };
  /* `/planung` ist seit dem 08.10.2026 der Kalender (docs/65) — eine
     Meldung von früher trägt noch den alten Weg. */
  /* Eine einzelne Ortsaufnahme (seit dem 08.10.2026 führt der Fall in ihre
     Akte, nicht in die Liste): „Zur Ortsaufnahme". */
  if (/^\/aufnahmen\/[^/?]+/.test(f.weg)) return { wort: "fall.weg.aufnahme", zu: f.weg };
  const roh = f.weg.split(/[/?]/)[1] ?? "";
  const teil = roh === "planung" ? "kalender" : roh;
  const wort = ["abrechnung", "kalender", "ressourcen", "kunden", "aufnahmen", "einstellungen", "angebote", "auftraege"]
    .includes(teil) ? `fall.weg.${teil}` : "fall.weg.oeffnen";
  return { wort, zu: f.weg };
}

/**
 * **Was eine leere Fallspalte sagt** (Prüfrunde Runde 10, N10-03). Direkt
 * nach der Anmeldung stand „Nichts, was bei dir liegt." und „Alles
 * verteilt.", während `GET /faelle` noch unterwegs war — eine Aussage, auf die
 * hin jemand den Tag beginnt. Solange nichts da ist, steht „Wird geholt …";
 * ging die Abfrage schief, sagt die Spalte das; erst eine Antwort ohne Fälle
 * ist ein Leerzustand.
 */
export function fallspaltenLeersatz(abfrage: Ladestand, leer: string): string {
  // Seit N11-01 der gemeinsame Baustein für jede Stelle (`daten/ladestand.ts`).
  return leersatzFuer(abfrage, leer, "fall.leer.fehler");
}
